'use client';

import { useEffect, useSyncExternalStore } from 'react';

import type { CookieConsentCategory, CookieConsentRegion } from '@repo/common';

import { CookieConsentBanner } from '../components/cookie-consent/cookie-consent-banner';
import { initializeApp } from '../utils/app-init';
import { COOKIE_CONSENT_ENABLED } from './cookie-consent-config';
import {
  getConsentResolvedSnapshot,
  getConsentSnapshot,
  getConsentViewSnapshot,
  getServerConsentResolvedSnapshot,
  getServerConsentSnapshot,
  getServerConsentViewSnapshot,
  hasCookieConsent,
  initializeCookieConsent,
  preloadCookieConsentLibrary,
  refreshStoredCookieConsent,
  subscribeToConsent,
  subscribeToConsentView,
} from './cookie-consent-engine';

import './cookie-consent.scss';

export type { CookieConsentCategory, CookieConsentOptionalCategory } from '@repo/common';

export function useCookieConsent(category: CookieConsentCategory): boolean {
  const state = useSyncExternalStore(subscribeToConsent, getConsentSnapshot, getServerConsentSnapshot);
  return COOKIE_CONSENT_ENABLED ? state[category] : hasCookieConsent(category);
}

/** True once the visitor has no consent banner or preferences panel open and none is still about to appear. */
export function useCookieConsentSettled(): boolean {
  const resolved = useSyncExternalStore(
    subscribeToConsentView,
    getConsentResolvedSnapshot,
    getServerConsentResolvedSnapshot,
  );
  const view = useSyncExternalStore(subscribeToConsentView, getConsentViewSnapshot, getServerConsentViewSnapshot);
  return resolved && view === 'hidden';
}

interface CookieConsentProviderProps {
  apiUri?: string;
  renderUi?: boolean;
  verifiedRegion?: CookieConsentRegion;
}

export function CookieConsentProvider({ apiUri, renderUi = true, verifiedRegion }: CookieConsentProviderProps = {}) {
  useEffect(() => {
    // Download the library in parallel with app initialization instead of after it.
    preloadCookieConsentLibrary();
    if (apiUri) {
      void initializeApp(apiUri);
    } else {
      void initializeCookieConsent(verifiedRegion);
    }
    const refresh = () => {
      void refreshStoredCookieConsent();
    };
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, [apiUri, verifiedRegion]);

  return COOKIE_CONSENT_ENABLED && renderUi ? <CookieConsentBanner /> : null;
}
